import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import SearchableSelect from './SearchableSelect.tsx';

const SkillSubstitutionGrid = forwardRef(({ 
  objective, 
  objectives, 
  completedSkills, 
  disabled = false,
  editMode = false,
  actualId = null
}, ref) => {
  const [skillRows, setSkillRows] = useState([]);
  const [totalDifficulty, setTotalDifficulty] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentActualId, setCurrentActualId] = useState('');

  // Expose getSkills function through ref
  useImperativeHandle(ref, () => ({
    getSkills: () => ({
      actualId: currentActualId,
      totalDifficulty: totalDifficulty
    })
  }));

  // Parse objective to get skills based on type
  const parseObjectiveToSkills = () => {
    if (!objective || !objective.id || !objectives || !Array.isArray(objectives)) return [];

    const skills = [];
    
    if (objective.type === 'routine') {
      // For routines, the ID is the routine ID, get all 10 skills
      const routineSkills = objective.id.split('-').map(Number);
      const skillsToShow = Math.min(completedSkills || 10, routineSkills.length);
      
      for (let i = 0; i < skillsToShow; i++) {
        const skillId = routineSkills[i].toString();
        const skillObj = objectives.find(obj => obj.id === skillId && obj.type === 'skill');
        if (skillObj) {
          skills.push({
            position: i + 1,
            originalId: skillId,
            currentId: skillId,
            name: skillObj.name,
            difficulty: skillObj.difficulty,
            included: true
          });
        }
      }
    } 
    else if (objective.type === 'section') {
      // For sections, parse the section ID and get parent routine info
      const sectionSkills = objective.id.split('-').map(Number);
      const skillsToShow = Math.min(completedSkills || sectionSkills.length, sectionSkills.length);
      
      for (let i = 0; i < skillsToShow; i++) {
        const skillId = sectionSkills[i].toString();
        const skillObj = objectives.find(obj => obj.id === skillId && obj.type === 'skill');
        if (skillObj) {
          skills.push({
            position: i + 1,
            originalId: skillId,
            currentId: skillId,
            name: skillObj.name,
            difficulty: skillObj.difficulty,
            included: true
          });
        }
      }
    }
    else if (objective.type === 'drill') {
      // For drills: length-repetitions-skill1-skill2-etc
      const drillParts = objective.id.split('-').map(Number);
      const length = drillParts[0];
      const repetitions = drillParts[1];
      const drillSkills = drillParts.slice(2);
      
      const totalSkills = length * repetitions;
      const skillsToShow = Math.min(completedSkills || totalSkills, totalSkills);
      
      let position = 1;
      for (let rep = 0; rep < repetitions && position <= skillsToShow; rep++) {
        for (let skill = 0; skill < drillSkills.length && position <= skillsToShow; skill++) {
          const skillId = drillSkills[skill].toString();
          const skillObj = objectives.find(obj => obj.id === skillId && obj.type === 'skill');
          if (skillObj) {
            skills.push({
              position: position,
              originalId: skillId,
              currentId: skillId,
              name: skillObj.name,
              difficulty: skillObj.difficulty,
              included: true,
              repetition: rep + 1
            });
            position++;
          }
        }
      }
    }
    
    return skills;
  };

  // Parse actualId to get actual skills for edit mode
  const parseActualIdToSkills = (actualIdString, originalSkills) => {
    if (!actualIdString || !originalSkills || originalSkills.length === 0) {
      return originalSkills;
    }

    const actualSkillIds = actualIdString.split('-');
    const updatedSkills = [...originalSkills];

    // Update each skill with the corresponding actual skill ID
    actualSkillIds.forEach((actualSkillId, index) => {
      if (index < updatedSkills.length && actualSkillId) {
        updatedSkills[index].currentId = actualSkillId;
      }
    });

    return updatedSkills;
  };

  // Initialize skill rows when objective changes
  useEffect(() => {
    const originalSkills = parseObjectiveToSkills();
    let finalSkills = originalSkills;

    // If in edit mode and actualId is provided, use it to populate actual skills
    if (editMode && actualId) {
      finalSkills = parseActualIdToSkills(actualId, originalSkills);
    }

    setSkillRows(finalSkills);
    
    // Set initial actual ID
    const initial = finalSkills.map(row => row.currentId).join('-');
    setCurrentActualId(initial);
  }, [objective, objectives, completedSkills, editMode, actualId]);

  // Calculate total difficulty and new ID when skill rows change
  useEffect(() => {
    if (!objectives || !Array.isArray(objectives)) return;
    
    const includedSkills = skillRows.filter(row => row.included);
    const newDifficulty = includedSkills.reduce((sum, row) => {
      const skill = objectives.find(obj => obj.id === row.currentId && obj.type === 'skill');
      return sum + (skill ? skill.difficulty : 0);
    }, 0);
    
    setTotalDifficulty(parseFloat(newDifficulty.toFixed(1)));
    
    // Generate actual ID based on current skills
    const newActualId = includedSkills.map(row => row.currentId).join('-');
    setCurrentActualId(newActualId);
  }, [skillRows, objectives]);

  const handleSubstitutionChange = (index, newSkillId) => {
    const newSkillRows = [...skillRows];
    newSkillRows[index].currentId = newSkillId;
    setSkillRows(newSkillRows);
  };

  // Get available skills for substitution (only 'skill' type)
  const availableSkills = objectives && Array.isArray(objectives) ? 
    objectives.filter(obj => obj.type === 'skill') : [];

  if (!objective || !objective.id || skillRows.length === 0) {
    return null;
  }

  const getObjectiveDisplayName = () => {
    switch (objective.type) {
      case 'routine':
        return `${objective.name} Skills`;
      case 'section':
        return `${objective.name} Skills`;
      case 'drill':
        return `${objective.name} Skills`;
      default:
        return 'Skills';
    }
  };

  return (
    <div className="skill-substitution-wrapper" style={{
      all: 'initial',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '14px',
      lineHeight: '1.5',
      color: '#374151',
      boxSizing: 'border-box',
      width: '100%'
    }}>
      <div 
        className={`border rounded-lg ${disabled ? 'bg-gray-100' : 'bg-white'} shadow-sm`}
        style={{
          border: '1px solid #d1d5db',
          borderRadius: '0.5rem',
          backgroundColor: disabled ? '#f3f4f6' : '#ffffff',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
        }}
      >
        <div
          className={`flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50`}
          onClick={() => setIsExpanded(!isExpanded)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem',
            cursor: 'pointer'
          }}
        >
          <div className="flex items-center space-x-2" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <h3 className="text-lg font-semibold" style={{
              fontSize: '1.125rem',
              fontWeight: '600',
              margin: 0
            }}>{getObjectiveDisplayName()}</h3>
            <span className="text-sm text-gray-600" style={{
              fontSize: '0.875rem',
              color: '#6b7280'
            }}>
              Total Difficulty: {totalDifficulty}
            </span>
          </div>
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
        
        {isExpanded && (
          <div className="pt-0">
            <div style={{
              width: '100%',
              
            }}>
              <table className="w-full border-collapse" style={{
                width: '100%',
                borderCollapse: 'collapse',
                tableLayout: 'fixed'
              }}>
                <thead>
                  <tr className="bg-gray-50" style={{
                    backgroundColor: '#f9fafb'
                  }}>
                    <th className="border p-2 text-left" style={{
                      border: '1px solid #d1d5db',
                      padding: '0.5rem',
                      textAlign: 'left',
                      width: '8%',
                      verticalAlign: 'top'
                    }}></th>
                    <th className="border p-2 text-left" style={{
                      border: '1px solid #d1d5db',
                      padding: '0.5rem',
                      textAlign: 'left',
                      width: '46%',
                      verticalAlign: 'top'
                    }}>Planned Skill</th>
                    <th className="border p-2 text-left" style={{
                      border: '1px solid #d1d5db',
                      padding: '0.5rem',
                      textAlign: 'left',
                      width: '46%',
                      verticalAlign: 'top'
                    }}>Actual Skill</th>
                  </tr>
                </thead>
                <tbody>
                  {skillRows.map((row, index) => {
                    const plannedSkill = objectives && Array.isArray(objectives) ? 
                      objectives.find(obj => obj.id === row.originalId && obj.type === 'skill') : null;
                    const isSubstituted = row.currentId !== row.originalId;
                    
                    return (
                      <tr key={index} className="hover:bg-gray-50" style={{
                        backgroundColor: 'transparent'
                      }}>
                        <td className="border p-2 text-center" style={{
                          border: '1px solid #d1d5db',
                          padding: '0.5rem',
                          textAlign: 'center',
                          verticalAlign: 'middle'
                        }}>
                          {row.position}
                        </td>
                        <td className="border p-2" style={{
                          border: '1px solid #d1d5db',
                          padding: '0.5rem',
                          verticalAlign: 'middle'
                        }}>
                          <div style={{
                            width: '100%',
                            lineHeight: '1.4',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word'
                          }}>
                            <span 
                              className={`${
                                isSubstituted ? 'text-red-600' : 'text-gray-900'
                              }`}
                              style={{
                                color: isSubstituted ? '#dc2626' : '#111827'
                              }}
                            >
                              {plannedSkill ? plannedSkill.name.split('(')[0] : 'Unknown Skill'}
                            </span>
                          </div>
                        </td>
                        <td className="border p-2" style={{
                          border: '1px solid #d1d5db',
                          padding: '0.5rem',
                          verticalAlign: 'middle'
                        }}>
                          <SearchableSelect
                            value={row.currentId}
                            onChange={(newSkillId) => handleSubstitutionChange(index, newSkillId)}
                            options={availableSkills}
                            disabled={disabled || !row.included}
                            placeholder="Search skills..."
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default SkillSubstitutionGrid;